"use client";

import ArticleContent from "../../../../components/ArticleContent";
import ArticlePreview from "../../../../components/ArticlePreview";
import { articleService } from "@/services/articleService";
import { Section } from "@/types/common";
import { ArticleData } from "@/types/article.types";
import { padding } from "@/services/commonFunction";
import Loader from "@/components/Loader";
// import ErrorPage from "../../../error";
import { AdsSection } from "@/components/Sections/AdsSection";
import { useCallback, useEffect, useRef, useState } from "react";
import { redirect } from "next/navigation";
import ArticleLoadMoreSection from "@/components/ArticleLoadMoreSection";
import { useArticleMeta } from "./useArticleMeta";

const RENDER_WINDOW = 3;
const MAX_ARTICLES = 5;
const ESTIMATED_ARTICLE_HEIGHT = 4000; // px

// ─── Helpers ──────────────────────────────────────────────────────────────────

function trackArticleView(article: { title?: string; fullpath?: string }) {
  if (typeof window === "undefined") return;
  window.dataLayer?.push({
    event: "virtual_page_view",
    page_title: article.title,
    page_path: article.fullpath,
  });
}

function NextStoryDivider() {
  return (
    <div
      className="flex items-center gap-4 py-10"
      role="separator"
      aria-label="Next story"
    >
      <span className="text-xs font-semibold uppercase tracking-widest text-orange shrink-0">
        Next story
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

function EndOfStories() {
  return (
    <div className="py-12 text-center">
      <p className="text-sm text-neutral-500">
        You&apos;ve reached the end of this story chain.
      </p>
    </div>
  );
}

// ─── Virtualized Article Item ─────────────────────────────────────────────────

interface VirtualArticleItemProps {
  item: ArticleData;
  index: number;
  isVisible: boolean;
  measuredHeight: number | null;
  host?: string;
  slug?: string;
  articleRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  heightRefs: React.MutableRefObject<Record<number, number>>;
  onHeightMeasured: (postId: number, height: number) => void;
}

/**
 * Renders the full article when visible, otherwise renders a spacer div
 * with the last-measured (or estimated) height to maintain scroll integrity.
 */
function VirtualArticleItem({
  item,
  index,
  isVisible,
  measuredHeight,
  host,
  slug,
  articleRefs,
  heightRefs,
  onHeightMeasured,
}: VirtualArticleItemProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Measure real height after mount / update
  useEffect(() => {
    if (!isVisible || !containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) onHeightMeasured(item.post_id, h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isVisible, item.post_id, onHeightMeasured]);

  // Build ad sections from item.ads
  let ArticlesAdsRight: Section | null = null;
  let ArticlesAdsLeft: Section | null = null;
  const ArticlesAds: Section[] = [];
  if (item?.ads) {
    for (const ad of item.ads) {
      if (ad.banner_id === "article-right-top-banner") ArticlesAdsRight = ad;
      else if (ad.banner_id === "article-left-top-banner") ArticlesAdsLeft = ad;
      else if (ad.banner_id === "article-left-bottom-banner")
        ArticlesAds.push(ad);
    }
  }

  const placeholderHeight =
    measuredHeight ??
    heightRefs.current[item.post_id] ??
    ESTIMATED_ARTICLE_HEIGHT;

  if (!isVisible) {
    // Render a lightweight spacer that preserves layout height
    return (
      <div
        key={item.post_id}
        ref={(el) => {
          articleRefs.current[item.post_id] = el;
        }}
        data-postid={item.post_id}
        style={{ height: placeholderHeight }}
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      key={item.post_id}
      ref={(el) => {
        articleRefs.current[item.post_id] = el;
        (
          containerRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = el;
      }}
      data-postid={item.post_id}
      className="max-w-4xl mx-auto font-sans w-full"
    >
      {index > 0 && <NextStoryDivider />}
      <ArticlePreview data={item} host={host} slug={item.slug || slug} />
      <ArticleContent data={item} ads={ArticlesAds} />
      {ArticlesAdsLeft && <AdsSection data={ArticlesAdsLeft} />}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ArticleComponent({
  params,
  host,
  data,
  isLoading,
}: {
  params: {
    slug?: string;
    category?: string;
    sub_category?: string;
    post_type?: string;
    brand_slug?: string;
    bucket?: string;
    tag?: string;
    sub_tag?: string;
  };
  host?: string;
  data: ArticleData;
  isLoading: boolean;
}) {
  const { post_type, slug } = params;

  // ── Meta updater ─────────────────────────────────────────────────────────
  const { updateMeta } = useArticleMeta({ host: host ?? "" });

  // ── State ────────────────────────────────────────────────────────────────
  const [articles, setArticles] = useState<ArticleData[]>([data]);
  // const router = useRouter();
  const [articleData, setArticleData] = useState<ArticleData>(data);
  const [brandId, setBrandId] = useState<number>(data?.brand?.brand_id || 0);
  const [usedPostIds, setUsedPostIds] = useState<number[]>([data?.post_id]);
  const [initialTrending, setInitialTrending] = useState<any[]>([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);
  const [hasNext, setHasNext] = useState(true);
  const [activeArticlePostId, setActiveArticlePostId] = useState<number | null>(
    data?.post_id ?? null,
  );

  /**
   * visibleRange = [startIndex, endIndex] (inclusive) within `articles`.
   * Only articles in this range are fully rendered; others get spacers.
   */
  const [visibleRange, setVisibleRange] = useState<[number, number]>([
    0,
    Math.min(RENDER_WINDOW - 1, 0),
  ]);

  // ── Refs ─────────────────────────────────────────────────────────────────
  const articleRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const measuredHeights = useRef<Record<number, number>>({});
  const lastHistoryPathRef = useRef<string | null>(null);
  const isPopStateNavigationRef = useRef(false);
  const isFetchingRef = useRef(false);
  // Mirror of activeArticlePostId so the IntersectionObserver callback can read
  // the latest active id without the observer effect depending on it (which
  // would tear down and rebuild the observer on every scroll).
  const activeArticlePostIdRef = useRef<number | null>(data?.post_id ?? null);
  useEffect(() => {
    activeArticlePostIdRef.current = activeArticlePostId;
  }, [activeArticlePostId]);

  const getArticlePath = useCallback((article: ArticleData) => {
    const fullPath = article.fullpath ?? "";
    return fullPath.startsWith("/") ? fullPath : `/${fullPath}`;
  }, []);

  const scrollToArticle = useCallback((postId: number) => {
    if (typeof window === "undefined") return;
    const target = articleRefs.current[postId];
    if (!target) return;
    target.scrollIntoView({ behavior: "auto", block: "start" });
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  // let isBroDoneFlag = 0;
  // const brand_ids = localStorage.getItem("brand_ids") ?? "";
  // const barndArr = brand_ids ? brand_ids.split(",") : [];
  // isBroDoneFlag =
  //   localStorage.getItem("broFormData") &&
  //   barndArr.includes(String(data?.brand?.brand_id))
  //     ? JSON.parse(localStorage.getItem("broFormData") as string).email
  //       ? 1
  //       : 0
  //     : 0;

  // ── Height tracking ───────────────────────────────────────────────────────

  const handleHeightMeasured = useCallback((postId: number, height: number) => {
    measuredHeights.current[postId] = height;
  }, []);

  // ── Fetch gated article content ───────────────────────────────────────────

  const getArticleData = useCallback(async () => {
    const response = await articleService.getArticleDataById(
      data?.post_id,
      host,
    );
    if (response?.data && response?.success) {
      const newContent =
        response.data.articleElement ?? response.data.json_content;
      setArticleData((prev) => ({
        ...prev,
        json_content: newContent ?? prev.json_content,
      }));
      setArticles((prev) =>
        prev.map((a) =>
          a.post_id === data?.post_id
            ? { ...a, json_content: newContent ?? a.json_content }
            : a,
        ),
      );
      if (response.data?.contentWall?.code !== "FORM") {
        setBrandId(response.data?.brand?.brand_id || 0);
      }
    }
  }, [data?.post_id, host]);
  const [isBroDoneFlag, setIsBroDoneFlag] = useState(0);

  useEffect(() => {
    const brandIds = localStorage.getItem("brand_ids") || "";
    const brandArr = brandIds ? brandIds.split(",") : [];
    const broFormData = localStorage.getItem("broFormData");

    if (!broFormData || !brandArr.includes(String(data?.brand?.brand_id))) {
      setIsBroDoneFlag(0);
      return;
    }

    try {
      const parsed = JSON.parse(broFormData);
      setIsBroDoneFlag(parsed?.email ? 1 : 0);
    } catch {
      setIsBroDoneFlag(0);
    }
  }, [data?.brand?.brand_id]);
  //commented this due the fullpath is not matching with the params as it was redirected to other category but fullpath has old category and middleware handles the redirect so commented
  // useEffect(() => {
  //   // Handle router replacement if needed
  //   if (
  //     "/" + params?.post_type + "/" + params?.category + "/" + params?.slug !==
  //     data?.fullpath
  //   ) {
  //     // console.log(
  //     //   "reload 2 =====>",
  //     //   "/" +
  //     //     params?.post_type +
  //     //     "/" +
  //     //     params?.category +
  //     //     "/" +
  //     //     params?.slug !==
  //     //     data?.fullpath,
  //     //   "/" + params?.post_type + "/" + params?.category + "/" + params?.slug,
  //     //   data?.fullpath
  //     // );
  //     router.replace(data?.fullpath);
  //   }
  // }, []);

  useEffect(() => {
    if (data?.brand && isBroDoneFlag === 1) {
      getArticleData();
    }
  }, [data?.brand, isBroDoneFlag]);

  // ── Fetch suggested articles once ─────────────────────────────────────────

  useEffect(() => {
    const fetchSuggested = async () => {
      try {
        const querySlug =
          slug +
          `?post_type=${post_type}&page_size=4&page_number=1&brandId=${brandId}&mode=both&usedPostIds=${usedPostIds.join(",")}`;
        const response = await articleService.getSuggestedArticles(querySlug);
        if (response?.data) {
          const newIds = [
            ...(response.data.trending || []),
            ...(response.data.related || []),
          ].map((a: any) => a.post_id);
          setUsedPostIds((prev) => [...prev, ...newIds]);
          setInitialTrending(response.data.trending || []);
        }
      } catch (error) {
        console.error("Error fetching suggested articles:", error);
      } finally {
        setInitialLoaded(true);
      }
    };
    fetchSuggested();
  }, []);

  // ── Load next article ─────────────────────────────────────────────────────

  const loadNextArticle = useCallback(async () => {
    if (
      loadingNext ||
      isFetchingRef.current ||
      !hasNext ||
      articles.length === 0 ||
      articles.length >= MAX_ARTICLES
    )
      return;
    const last = articles[articles.length - 1];
    const lastSlug =
      last?.slug ??
      last?.fullpath?.split("/").filter(Boolean).pop() ??
      slug ??
      "";
    if (!lastSlug) return;
    
    isFetchingRef.current = true;
    setLoadingNext(true);
    try {
      const response = await articleService.getNextArticle(
        lastSlug,
        articles.map((a) => a.post_id),
        host,
      );
      const payload = response?.data ?? response;
      const nextArticle = payload?.article ?? null;
      const nextHasNext = payload?.hasNext !== false;
      if (nextArticle && response?.success !== false) {
        setArticles((prev) => {
          if (prev.some((a) => a.post_id === nextArticle.post_id)) {
            return prev;
          }
          return [...prev, nextArticle];
        });
        setUsedPostIds((prev) => [...new Set([...prev, nextArticle.post_id])]);
        
        // Safety catch: if the newly returned article is already in the list, stop loading more to prevent loops
        if (articles.some((a) => a.post_id === nextArticle.post_id)) {
          setHasNext(false);
        } else {
          setHasNext(!!nextHasNext && !!nextArticle);
        }
      } else {
        setHasNext(!!nextHasNext && !!nextArticle);
      }
    } catch {
      setHasNext(false);
    } finally {
      isFetchingRef.current = false;
      setLoadingNext(false);
    }
  }, [articles, hasNext, loadingNext, slug, host]);

  // ── Bidirectional virtualization: track which article is active & update render window ──

  useEffect(() => {
    const refs = articleRefs.current;
    const nodes = Object.values(refs).filter(
      (node): node is HTMLDivElement => node != null,
    );
    if (nodes.length === 0) return;

    const postIdToPath = new Map<number, string>();
    const postIdToIndex = new Map<number, number>();
    articles.forEach((a, i) => {
      postIdToIndex.set(a.post_id, i);
      const path = a.fullpath;
      if (path)
        postIdToPath.set(a.post_id, path.startsWith("/") ? path : `/${path}`);
    });

    const ratiosByPostId = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const postIdRaw = (entry.target as HTMLElement).getAttribute(
            "data-postid",
          );
          if (postIdRaw == null) continue;
          const postId = Number(postIdRaw);
          if (Number.isNaN(postId)) continue;
          ratiosByPostId.set(
            postId,
            entry.isIntersecting ? entry.intersectionRatio : 0,
          );
        }

        let bestPostId: number | null = null;
        let bestRatio = 0;
        ratiosByPostId.forEach((ratio, postId) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPostId = postId;
          }
        });

        if (bestPostId === null || typeof window === "undefined") return;

        // Update active article, URL/title, analytics and meta — but only
        // when the active article actually changes (prevents duplicate
        // virtual_page_view events and redundant DOM writes on every tick).
        if (bestPostId !== activeArticlePostIdRef.current) {
          const article = articles.find((a) => a.post_id === bestPostId);
          if (article) {
            activeArticlePostIdRef.current = bestPostId;
            setActiveArticlePostId(bestPostId);
            trackArticleView(article);
            updateMeta(article); // single call updates title + tags + JSON-LD
          }
        }

        // ── Bidirectional virtualization: expand render window around active article ──
        const activeIndex = postIdToIndex.get(bestPostId);
        if (activeIndex == null) return;

        const half = Math.floor(RENDER_WINDOW / 2);
        const newStart = Math.max(0, activeIndex - half);
        const newEnd = Math.min(articles.length - 1, activeIndex + half);

        setVisibleRange(([prevStart, prevEnd]) => {
          if (prevStart === newStart && prevEnd === newEnd)
            return [prevStart, prevEnd];
          return [newStart, newEnd];
        });
      },
      {
        rootMargin: "-40% 0px -40% 0px",
        threshold: [0, 0.25, 0.5, 0.75],
      },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [articles, updateMeta]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (lastHistoryPathRef.current === null) {
      lastHistoryPathRef.current = window.location.pathname;
      return;
    }

    if (activeArticlePostId === null) return;

    const activeArticle = articles.find(
      (article) => article.post_id === activeArticlePostId,
    );
    if (!activeArticle) return;

    const fullPath = getArticlePath(activeArticle);
    if (!fullPath || fullPath === lastHistoryPathRef.current) return;

    if (isPopStateNavigationRef.current) {
      isPopStateNavigationRef.current = false;
      lastHistoryPathRef.current = fullPath;
      return;
    }

    window.history.pushState(
      { ...window.history.state, as: fullPath, url: fullPath },
      "",
      fullPath,
    );
    lastHistoryPathRef.current = fullPath;
  }, [activeArticlePostId, articles, getArticlePath]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handlePopState = () => {
      const currentPath = window.location.pathname;
      const matchedArticle = articles.find(
        (article) => getArticlePath(article) === currentPath,
      );

      if (!matchedArticle) return;

      isPopStateNavigationRef.current = true;
      setActiveArticlePostId(matchedArticle.post_id);
      if (matchedArticle.title) document.title = matchedArticle.title;
      updateMeta(matchedArticle);
      scrollToArticle(matchedArticle.post_id);
      lastHistoryPathRef.current = currentPath;
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [articles, getArticlePath, scrollToArticle, updateMeta]);

  // ── Expand visible window when new articles are appended ──────────────────

  // useEffect(() => {
  //   setVisibleRange(([start, end]) => {
  //     // When a new article is appended keep the window anchored but allow it to
  //     // extend to cover the newly added item if it is adjacent to the current end.
  //     const newEnd = Math.min(articles.length - 1, end + 1);
  //     return [start, newEnd];
  //   });
  // }, [articles.length]);

  useEffect(() => {
    setVisibleRange(([start]) => [start, articles.length - 1]);
  }, [articles.length]);

  // ── Smart Buffered Loading ──────────────────────────────────────────────
  // Maintains a buffer of 2 articles ahead of the user's current reading position
  // to completely eliminate loading spinners while scrolling.
  
  useEffect(() => {
    if (!hasNext || loadingNext || articles.length >= MAX_ARTICLES) return;

    const activeIndex = articles.findIndex((a) => a.post_id === activeArticlePostId);
    const currentIdx = activeIndex >= 0 ? activeIndex : 0;
    const articlesAhead = articles.length - 1 - currentIdx;
    const BUFFER_SIZE = 2;

    if (articlesAhead < BUFFER_SIZE) {
      loadNextArticle();
    }
  }, [activeArticlePostId, articles, hasNext, loadingNext, loadNextArticle]);

  // ── Guard ─────────────────────────────────────────────────────────────────

  if (!articleData && !isLoading) {
    console.log("articleData===>", JSON.stringify(articleData,null,2));
    const msg = (articleData as any)?.message || "Article-not-found3";
    redirect('/404?msg='+msg);
    // notFound();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <section
      className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-0 ${padding?.py}`}
    >
      {!isLoading && articleData ? (
        <div className="articleBody w-full">
          <div className="articleBody w-full grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-16">
            <div className="flex flex-col">
              {articles.map((item, index) => {
                const isVisible =
                  index >= visibleRange[0] && index <= visibleRange[1];
                return (
                  <VirtualArticleItem
                    key={item.post_id}
                    item={item}
                    index={index}
                    isVisible={isVisible}
                    measuredHeight={
                      measuredHeights.current[item.post_id] ?? null
                    }
                    host={host}
                    slug={slug}
                    articleRefs={articleRefs}
                    heightRefs={measuredHeights}
                    onHeightMeasured={handleHeightMeasured}
                  />
                );
              })}

              {loadingNext && (
                <div className="min-h-[120px] flex items-center justify-center">
                  <Loader />
                </div>
              )}


              {!hasNext && articles.length > 0 && <EndOfStories />}
            </div>

            {/* Sticky sidebar */}
            <div className="w-full sticky top-24 h-fit flex flex-col gap-8">
              {(() => {
                const activeArticle =
                  articles.find((a) => a.post_id === activeArticlePostId) ??
                  articles[0];
                const ArticlesAdsRight = activeArticle?.ads?.find(
                  (ad) => ad.banner_id === "article-right-top-banner",
                );
                return ArticlesAdsRight ? (
                  <AdsSection data={ArticlesAdsRight} />
                ) : null;
              })()}
              <ArticleLoadMoreSection
                type="Trending"
                usedPostIds={usedPostIds}
                setUsedPostIds={setUsedPostIds}
                slug={slug as string}
                post_type={post_type as string}
                brandId={brandId}
                initialArticles={initialTrending}
                initialLoaded={initialLoaded}
                lineheight="1.25rem"
              />
            </div>
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </section>
  );
}
